import { publicApi } from "@api";
import { IconMSIPhoneAndroid } from "@assets";
import { OnboardCallbackProps } from "@components/utils/Auth/types";
import { envConfig } from "@configs/env";
import { OTPSource, useStatsigTracking } from "@configs/statsig";
import { OnboardMode, ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useOnboardingPhone } from "@hooks/useOnboardingPhone";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getStoredCredential } from "@utils/credentials";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  APIErrorCodes,
  Button,
  TextField,
  delay,
  localizePhoneNumber,
  standardizePhoneNumber,
  useDateAvailCheck,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OnboardingModeRadioGroup } from "../../..";

type LoginFormType = {
  phone: string;
};

interface OnboardPPProps extends OnboardCallbackProps {
  onLogin: (phone?: string) => void;
  onRegister: (phone?: string) => void;
}

const OnboardPP = ({ defaultMode = OnboardMode.LOGIN, onLogin, onRegister }: OnboardPPProps) => {
  const { t, i18n } = useTranslation();
  const { trackOTPSent, trackOTPRequestFailed } = useStatsigTracking();
  const [mode, setMode] = useState<OnboardMode>(defaultMode);
  const [createRegisterMutation, { isLoading: isCreateRegisterLoading }] = publicApi.useCreateRegisterMutation();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const { updatePhone: updateOnboardingPhone } = useOnboardingPhone();
  const { phoneMinLength, phoneMaxLength, phonePrefix, phoneStartWith } = useMemberSettings();

  const loginFormSchema = useMemo(() => {
    return z.object({
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        msgType: "phone",
      }),
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith]);

  // sync with defaultMode
  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const loginCredId = localizePhoneNumber(getStoredCredential().clientId ?? "", phonePrefix);

  const {
    handleSubmit: handlePhoneSubmit,
    register: formPhoneRegister,
    formState: { errors: formPhoneErrors },
  } = useForm<LoginFormType>({
    mode: "onChange",
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      phone: loginCredId,
    },
  });

  const { check: checkRequestOTP } = useDateAvailCheck(loginCredId);

  const submitting = useRef(false);

  const onSubmitPhone = async ({ phone }: LoginFormType) => {
    if (submitting.current) return;
    submitting.current = true;

    let allowRedirect = true;

    updateOnboardingPhone(phone);

    if (mode === OnboardMode.REGISTER) {
      if (checkRequestOTP()) {
        const createRegisterResponse = await createRegisterMutation({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
        });

        if (createRegisterResponse.error) {
          const errorData = (createRegisterResponse.error as FetchBaseQueryError).data as {
            error: { message: string };
          };
          const errorMessage = errorData?.error?.message;

          if (errorMessage === APIErrorCodes.VerifyRequest.RateLimited || errorMessage?.startsWith("unauthorized")) {
            allowRedirect = false;
          }
        }

        handleApiResponse(createRegisterResponse, {
          toastSuccess: false,
          onSuccess: () => {
            if (createRegisterResponse?.data?.method) {
              // Track OTP sent success
              trackOTPSent(createRegisterResponse.data.method, { source: OTPSource.REGISTER });

              toast.success(getOTPCodeSentMessage(createRegisterResponse.data.method, i18n.language, t));
            }
          },
          toastError: false,
          onError: (error) => {
            const message = resolveAuthResponseError(error, t);
            trackOTPRequestFailed(error, { source: OTPSource.REGISTER });
            if (message) {
              toast.error(message);
            }
          },
        });
      }
    }
    submitting.current = false;

    if (allowRedirect) {
      if (mode === OnboardMode.LOGIN) {
        onLogin(phone);
      } else {
        onRegister(phone);
      }
    }
  };

  return (
    <>
      <OnboardingModeRadioGroup
        value={mode}
        onChange={setMode}
      />
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col items-center">
          <form
            onSubmit={handlePhoneSubmit((e) => delay().then(() => onSubmitPhone(e)))}
            className="w-full"
          >
            <TextField
              {...formPhoneRegister("phone")}
              error={!!formPhoneErrors.phone}
              helperText={t(formPhoneErrors.phone?.message ?? "", {
                minLength: phoneMinLength,
                maxLength: phoneMaxLength,
                startWith: "0",
              })}
              placeholder={t("Phone number")}
              type={"number"}
              inputMode={"numeric"}
              icon={<IconMSIPhoneAndroid className="text-content-base" />}
              classes={{
                "input&": "w-full !px-0 text-center",
                "iconContainer&": "!left-3 !top-[13px]",
              }}
              helperTextClasses={{
                className: "!text-center",
              }}
              size="lg"
            />

            <Button
              id="btn-next"
              variant="primary"
              size="lg"
              type="submit"
              className="mt-6"
              loading={isCreateRegisterLoading}
            >
              {t("Next")}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default OnboardPP;
